import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { be_regions, fr_regions, lu_regions } from "bundles/common/utils/constants";

export const STAGE_OPTIONS = [
  { label: "Ready to extract", value: "ready", statuses: ["ready", "failed", "cancelled"] },
  { label: "Extracting", value: "running", statuses: ["queued", "parsing", "uploaded", "extracting_text", "ocring"] },
  { label: "Extracted", value: "parsed", statuses: ["parsed"] },
  { label: "Candidates", value: "converted", statuses: ["converted"] },
  { label: "Needs OCR", value: "needs_ocr", statuses: ["needs_ocr"] },
];

export const SCANNED_OPTIONS = [
  { label: "Read by OCR", value: "true" },
  { label: "Not a scan", value: "false" },
];

const humanizeRegion = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const regionOptions = (keys) => keys.map((key) => ({ value: key, label: humanizeRegion(key) }));

const chipSx = (active) => ({
  fontWeight: 600,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 13,
  borderRadius: "0.6rem",
  height: 34,
  px: 0.5,
  cursor: "pointer",
  transition: "opacity 0.15s ease",
  ...(active
    ? {
      backgroundColor: "hsla(150, 38%, 17%, 0.08)",
      color: "hsl(150 38% 17%)",
      borderColor: "hsl(150 38% 17%)",
      "&:hover": { opacity: 0.8 },
    }
    : {
      backgroundColor: "#fff",
      color: "hsl(150 30% 12%)",
      borderColor: "hsl(140 8% 88%)",
      "&:hover": { borderColor: "hsl(140 8% 82%)" },
    }),
});

const optionLabelSx = {
  fontSize: 13,
  color: "hsl(150 30% 12%)",
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
};

const checkboxSx = {
  color: "hsl(140 8% 82%)",
  "&.Mui-checked": { color: "hsl(150 38% 17%)" },
};

const FilterChip = ({ def, selected, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const count = selected.length;

  const handleToggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const renderOption = (opt) => (
    <FormControlLabel
      key={opt.value}
      sx={{ display: "flex", mx: 0, px: 2, py: 0.25 }}
      control={
        <Checkbox
          size="small"
          disableRipple
          checked={selected.includes(opt.value)}
          onChange={() => handleToggle(opt.value)}
          sx={checkboxSx}
        />
      }
      label={<Typography sx={optionLabelSx}>{opt.label}</Typography>}
    />
  );

  return (
    <>
      <Chip
        variant="outlined"
        label={count > 0 ? `${def.label} (${count})` : def.label}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={chipSx(count > 0)}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "0.75rem",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              minWidth: 220,
              maxHeight: 360,
              overflow: "auto",
            },
          },
        }}
      >
        <Box sx={{ py: 0.5 }}>
          {def.grouped
            ? def.groups.map((group) => (
              <Box key={group.label}>
                <Typography
                  sx={{
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                  }}
                >
                  {group.label}
                </Typography>
                {group.options.map(renderOption)}
              </Box>
            ))
            : def.options.map(renderOption)}
        </Box>
      </Popover>
    </>
  );
};

const CvTableFilters = ({ filters, onFiltersChange, owners = [] }) => {
  const filterDefs = useMemo(
    () => [
      {
        id: "owner",
        label: "Uploaded by",
        options: owners.map((user) => ({ value: user.id, label: user.full_name || user.email })),
      },
      { id: "stage", label: "Stage", options: STAGE_OPTIONS },
      { id: "scanned", label: "OCR", options: SCANNED_OPTIONS },
      {
        id: "region",
        label: "Region",
        grouped: true,
        groups: [
          { label: "Belgium", options: regionOptions(be_regions) },
          { label: "Luxembourg", options: regionOptions(lu_regions) },
          { label: "France", options: regionOptions(fr_regions) },
        ],
      },
    ],
    [owners],
  );

  const totalActive = useMemo(
    () => Object.values(filters).reduce((sum, arr) => sum + (arr?.length || 0), 0),
    [filters],
  );

  const handleChange = useCallback(
    (filterId) => (newValues) => {
      onFiltersChange({ ...filters, [filterId]: newValues });
    },
    [filters, onFiltersChange],
  );

  const handleClearAll = useCallback(() => {
    onFiltersChange({ ...EMPTY_FILTERS });
  }, [onFiltersChange]);

  return (
    <Box
      sx={{
        px: 3,
        py: 1,
        backgroundColor: "#fff",
        borderBottom: "1px solid hsl(140 8% 88%)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <FilterListIcon sx={{ fontSize: 18, color: "hsl(150 30% 12%)", opacity: 0.5, mr: 0.5 }} />

        {filterDefs.map((def) => (
          <FilterChip
            key={def.id}
            def={def}
            selected={filters[def.id] || []}
            onChange={handleChange(def.id)}
          />
        ))}

        {totalActive > 0 && (
          <Chip
            size="small"
            label="Clear all"
            deleteIcon={<CloseIcon sx={{ fontSize: "14px !important", color: "hsl(0 72% 51%) !important" }} />}
            onDelete={handleClearAll}
            onClick={handleClearAll}
            sx={{
              ml: 0.5,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "hsl(0 72% 51%)",
              borderColor: "hsl(0 72% 51%)",
              backgroundColor: "transparent",
              height: 28,
              borderRadius: "0.6rem",
              transition: "opacity 0.15s ease",
              "&:hover": { backgroundColor: "hsla(0, 72%, 51%, 0.06)", opacity: 0.9 },
            }}
            variant="outlined"
          />
        )}
      </Stack>
    </Box>
  );
};

export const EMPTY_FILTERS = {
  owner: [],
  stage: [],
  scanned: [],
  region: [],
};

export default CvTableFilters;
